import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import howtouseglap from "../../assets/how_to_use_glap.png";
import { HeadingText } from "../../utils/CustomTagComponents";
import VideoPlayer from "../../components/common/VideoPlayer";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const InfoSection = ({
  title,
  desc,
  desc_section_image,
  desc_section_image_type = "image",
  how_use_title,
  how_use_desc,
  how_use_section_image,
  how_use_section_image_type = "image",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [ref1, inView1] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [ref2, inView2] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [ref3, inView3] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [ref4, inView4] = useInView({ triggerOnce: true, threshold: 0.2 });

  const fadeLeft = {
    hidden: { opacity: 0, x: -40 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
  };

  const fadeRight = {
    hidden: { opacity: 0, x: 40 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
  };

  // ✅ CLEAN TITLE
  const getResponsiveTitle = (html) => {
    if (!html) return "";

    return html
      // .replace(/style="[^"]*"/gi, "")
      .replace(
        /<(?!ol\b|ul\b)([a-z][a-z0-9]*)\b([^>]*)\sstyle="[^"]*"([^>]*)>/gi,
        "<$1$2$3>"
      )
      .replace(/font-size:\s*\d+px;?/gi, "")
      .replace(/font-weight:\s*900;?/gi, "font-weight:700");
  };

  // ✅ CLEAN DESCRIPTION
  const cleanHTML = (html) => {
    if (!html) return "";

    return html
      // .replace(/style="[^"]*"/gi, "")
      .replace(
        /<(?!ol\b|ul\b)([a-z][a-z0-9]*)\b([^>]*)\sstyle="[^"]*"([^>]*)>/gi,
        "<$1$2$3>"
      )
      .replace(/<p><br><\/p>/gi, "")
      .replace(/&nbsp;/gi, " ");
  };

  // ✅ CONTENT STYLING (KEY PART)
  const contentStyles = {
    fontSize: { xs: 14, sm: 15, md: 16 },
    lineHeight: 1.625,
    textAlign: "justify",

    "& p": {
      marginBlockStart: '0px !important',
      marginBottom: "16px",
    },

    "& p:last-of-type": {
      marginBottom: "0px",
    },

    // ✅ REMOVE DEFAULT BULLETS
    "& ul": {
      listStyle: "none",
      paddingLeft: "0px",
      marginBottom: "12px",
    },

    // ✅ CUSTOM LIST ITEM
    "& li": {
      position: "relative",
      paddingLeft: "28px",
      marginBottom: "4px",
      lineHeight: 1.7,
    },
    // ✅ FIRST ITEM
    "& li:first-of-type": {
      marginTop: "-10px",
    },

    // ✅ LAST ITEM
    "& li:last-of-type": {
      marginBottom: "15px",
    },

    // ✅ ADD ARROW ICON
    "& li::before": {
      content: '"\\f101"',
      fontFamily: "FontAwesome",
      position: "absolute",
      left: '5px',
      top: "2px",
      fontSize: "14px",
      color: "#fd0000",
    },

    "& h1, & h2, & h3, & h4": {
      marginTop: "20px",
      marginBottom: "12px",
      fontWeight: 600,
    },

    "& strong": {
      fontWeight: 600,
    },
  };

  return (
    <Container
      sx={{
        overflow: "hidden",
        py: { xs: 2, sm: 2, md: 3 },
        px: { xs: 3, sm: 4, md: 6 },
        paddingLeft: { xs: "24px !important", md: "48px !important" },
        paddingRight: { xs: "24px !important", md: "48px !important" },
      }}
    >
      {/* ---------- SECTION 1 (LEFT IMAGE / RIGHT TEXT) ---------- */}
      <Grid container spacing={2}>

        <Grid
          component={motion.div}
          ref={ref1}
          initial="hidden"
          animate={inView1 ? "visible" : "hidden"}
          variants={fadeLeft}
          size={{ xs: 12, md: 5 }}
          justifyContent={{ xs: "center", lg: "start" }}
          display={"flex"}
        >
          {desc_section_image && desc_section_image_type === "image" && (
            <Box
              component="img"
              src={desc_section_image}
              alt="Person Thinking"
              sx={{
                width: "100%",
                maxWidth: { xs: "100%", sm: "100%", md: 550 },
                borderRadius: 4,
                objectFit: "cover",
              }}
            />
          )}

          {desc_section_image && desc_section_image_type === "video" && (
            <VideoPlayer
              src={desc_section_image}
              width="100%"
              height={{ xs: 300, sm: 450, md: 540 }}
              iconSize={{ xs: 40, sm: 50, md: 60 }}
            />
          )}
        </Grid>

        {/* RIGHT TEXT */}
        <Grid
          component={motion.div}
          ref={ref2}
          initial="hidden"
          animate={inView2 ? "visible" : "hidden"}
          variants={fadeRight}
          size={{ xs: 12, md: 7 }}
        >
          <Box>
            <Box
              sx={{
                display: "inline-block",
                position: "relative",
                mb: 2,

                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  bottom: -8,
                  width: "200px",
                  height: "2px",
                  backgroundColor: "#fd0000",
                  opacity: "0.5"
                },
              }}
            >
              <HeadingText
                dangerouslySetInnerHTML={{
                  __html: getResponsiveTitle(title),
                }}
                sx={{
                  fontSize: { xs: "22px", sm: "26px", md: "30px" },
                  lineHeight: '1.3 !important',
                  fontWeight: 700,
                }}
              />
            </Box>
            <Typography
              component="div"
              sx={contentStyles}
              dangerouslySetInnerHTML={{
                __html: cleanHTML(desc),
              }}
            />
          </Box>
        </Grid>
      </Grid>

      {/* ---------- SECTION 2 (LEFT TEXT / RIGHT IMAGE) ---------- */}
      <Grid
        container
        spacing={6}
        alignItems="center"
        sx={{ mt: { xs: 3, sm: 4, md: 6 } }}
      >
        {/* LEFT TEXT */}
        <Grid
          component={motion.div}
          ref={ref3}
          initial="hidden"
          animate={inView3 ? "visible" : "hidden"}
          variants={fadeLeft}
          size={{ xs: 12, md: 7 }}
        >
          <Box>
            <Box
              sx={{
                display: "inline-block",
                position: "relative",
                mb: 2,

                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  bottom: -5,
                  width: "150px",
                  height: "2px",
                  backgroundColor: "#fd0000",
                  opacity: "0.5"
                },
              }}
            >
              <HeadingText
                dangerouslySetInnerHTML={{
                  __html: getResponsiveTitle(how_use_title),
                }}
                sx={{
                  fontSize: { xs: "22px", sm: "26px", md: "30px" },
                  lineHeight: '1.3 !important',
                  fontWeight: 700,
                }}
              />
            </Box>

            <Typography
              component="div"
              sx={contentStyles}
              dangerouslySetInnerHTML={{
                __html: cleanHTML(how_use_desc),
              }}
            />
          </Box>
        </Grid>

        {/* RIGHT IMAGE */}
        <Grid
          component={motion.div}
          ref={ref4}
          initial="hidden"
          animate={inView4 ? "visible" : "hidden"}
          variants={fadeRight}
          size={{ xs: 12, md: 5 }}
          justifyContent={{ xs: "center", lg: "end" }}
          display={"flex"}
        >
          {how_use_section_image && how_use_section_image_type === "image" && (
            <Box
              component="img"
              src={howtouseglap}
              alt="Person Thinking"
              sx={{
                width: "100%",
                maxWidth: { xs: "100%", sm: "100%", md: 550 },
                borderRadius: 4,
                objectFit: "cover",
              }}
            />
          )}

          {how_use_section_image && how_use_section_image_type === "video" && (
            <VideoPlayer
              src={how_use_section_image}
              width="100%"
              height={{ xs: 300, sm: 450, md: 540 }}
              iconSize={{ xs: 40, sm: 50, md: 60 }}
            />
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default InfoSection;